import React, { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Send, Trash2, RefreshCw, Ban, AlertTriangle, ShieldCheck, ChevronLeft, ChevronRight, ScrollText } from "lucide-react";
import { toast } from "sonner";

const STATUS_CLS = {
  queued: "bg-amber-100 text-amber-700", sent: "bg-emerald-100 text-emerald-700",
  failed: "bg-rose-100 text-rose-700", cancelled: "bg-slate-200 text-slate-600",
};
const EVENT_TYPES = ["all", "new_work_order", "work_order_created", "work_order_assigned", "job_assigned", "work_completed", "invoice_sent", "org_invitation", "test", "none"];

export default function EmailQueue() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [audit, setAudit] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ status: "queued", type: "all", recipient: "", demo: "all", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [confirm, setConfirm] = useState(null); // {action, ids, count, recipients, demoCount}
  const [showAudit, setShowAudit] = useState(false);

  const buildQuery = useCallback((pg) => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all" && v !== "") p.append(k, v); });
    p.append("page", pg); p.append("page_size", 25);
    return p.toString();
  }, [filters]);

  const load = useCallback((pg = page) => {
    setBusy(true);
    api.get(`/admin/email-queue?${buildQuery(pg)}`).then(({ data }) => { setData(data); setSel(new Set()); })
      .catch(() => setData(false)).finally(() => setBusy(false));
  }, [buildQuery, page]);
  const loadSummary = useCallback(() => api.get("/admin/email-queue/summary").then(({ data }) => setSummary(data)).catch(() => {}), []);
  const loadAudit = useCallback(() => api.get("/admin/email-queue/audit").then(({ data }) => setAudit(data)).catch(() => {}), []);

  useEffect(() => { load(1); setPage(1); loadSummary(); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  const items = data?.items || [];
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllVisible = () => setSel(new Set(items.map((i) => i.id)));
  const clearSel = () => setSel(new Set());

  const openConfirm = (action) => {
    const ids = [...sel];
    if (!ids.length) return toast.error("No emails selected");
    const chosen = items.filter((i) => sel.has(i.id));
    const recipients = new Set(chosen.map((i) => i.to));
    const demoCount = chosen.filter((i) => i.is_demo).length;
    setConfirm({ action, ids, count: ids.length, recipients: recipients.size, demoCount });
  };

  const runAction = async () => {
    if (!confirm) return;
    const { action, ids } = confirm;
    setBusy(true); setConfirm(null);
    try {
      const ep = { send: "send", retry: "retry", delete: "delete", cancel: "cancel" }[action];
      const { data: res } = await api.post(`/admin/email-queue/${ep}`, { ids });
      if (action === "send" || action === "retry") toast.success(`Sent ${res.sent}/${res.processed}${res.failed ? `, ${res.failed} failed` : ""}`);
      else if (action === "delete") toast.success(`Deleted ${res.deleted}`);
      else toast.success(`Cancelled ${res.cancelled}`);
      load(); loadSummary(); loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    setBusy(false);
  };

  const [demoPreview, setDemoPreview] = useState(null);
  const previewDemo = async () => {
    try { const { data } = await api.post("/admin/email-queue/delete-demo", { confirm: false }); setDemoPreview(data); }
    catch (e) { toast.error("Failed to preview"); }
  };
  const confirmDeleteDemo = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/email-queue/delete-demo", { confirm: true }); toast.success(`Deleted ${data.deleted} test/demo emails`); setDemoPreview(null); load(); loadSummary(); loadAudit(); }
    catch (e) { toast.error("Failed"); }
    setBusy(false);
  };

  if (data === false) return <DashboardLayout nav={getNav(user.role)} title="Email Queue"><p>Unable to load.</p></DashboardLayout>;

  return (
    <DashboardLayout nav={getNav(user.role)} title="Email Queue">
      <div className="space-y-5">
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="eq-summary">
            <Stat label="Total" value={summary.total} />
            <Stat label="Queued" value={summary.by_status?.queued || 0} cls="text-amber-600" />
            <Stat label="Sent" value={summary.by_status?.sent || 0} cls="text-emerald-600" />
            <Stat label="Test / Demo" value={summary.demo_count} cls="text-rose-600" />
            <Stat label="Legitimate" value={summary.legit_count} cls="text-blue-600" />
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
          <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })}
            options={[["all", "All"], ["queued", "Queued"], ["sent", "Sent"], ["failed", "Failed"], ["cancelled", "Cancelled"]]} testid="eq-filter-status" />
          <FilterSelect label="Type" value={filters.type} onChange={(v) => setFilters({ ...filters, type: v })}
            options={EVENT_TYPES.map((t) => [t, t === "all" ? "All" : t === "none" ? "(none)" : t])} testid="eq-filter-type" />
          <FilterSelect label="Data" value={filters.demo} onChange={(v) => setFilters({ ...filters, demo: v })}
            options={[["all", "All"], ["true", "Test/Demo only"], ["false", "Legitimate only"]]} testid="eq-filter-demo" />
          <div><label className="text-xs text-slate-500 block mb-1">Recipient</label>
            <Input data-testid="eq-filter-recipient" value={filters.recipient} onChange={(e) => setFilters({ ...filters, recipient: e.target.value })} placeholder="email…" className="h-9 w-44" /></div>
          <div><label className="text-xs text-slate-500 block mb-1">From</label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-9" /></div>
          <div><label className="text-xs text-slate-500 block mb-1">To</label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-9" /></div>
          <Button variant="ghost" size="sm" onClick={() => load()} data-testid="eq-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>

        {/* Bulk action bar */}
        <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
          <span className="text-sm font-medium text-slate-600 mr-2" data-testid="eq-selected-count">{sel.size} selected</span>
          <Button size="sm" variant="outline" onClick={selectAllVisible} data-testid="eq-select-all">Select All Visible</Button>
          <Button size="sm" variant="outline" onClick={clearSel} data-testid="eq-clear-sel">Clear Selection</Button>
          <div className="w-px h-6 bg-slate-200 mx-1" />
          <Button size="sm" onClick={() => openConfirm("send")} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="eq-send-selected"><Send className="w-3.5 h-3.5 mr-1" />Send Selected</Button>
          <Button size="sm" variant="outline" onClick={() => openConfirm("retry")} disabled={busy} data-testid="eq-retry-selected"><RefreshCw className="w-3.5 h-3.5 mr-1" />Retry</Button>
          <Button size="sm" variant="outline" onClick={() => openConfirm("cancel")} disabled={busy} data-testid="eq-cancel-selected"><Ban className="w-3.5 h-3.5 mr-1" />Mark Cancelled</Button>
          <Button size="sm" variant="outline" onClick={() => openConfirm("delete")} disabled={busy} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="eq-delete-selected"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => setShowAudit(true)} data-testid="eq-audit-btn"><ScrollText className="w-3.5 h-3.5 mr-1" />Audit Log</Button>
          <Button size="sm" onClick={previewDemo} className="bg-rose-600 hover:bg-rose-700" data-testid="eq-delete-demo-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete Test/Demo Queue</Button>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {busy && !data ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> : items.length === 0 ? (
            <p className="text-sm text-slate-400 p-8 text-center">No emails match these filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="eq-table">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="p-3 w-8"><Checkbox checked={items.length > 0 && sel.size === items.length} onCheckedChange={(c) => c ? selectAllVisible() : clearSel()} data-testid="eq-header-check" /></th>
                    <th className="p-3 text-left">Recipient</th>
                    <th className="p-3 text-left">Event</th>
                    <th className="p-3 text-left">Subject</th>
                    <th className="p-3 text-left">Related</th>
                    <th className="p-3 text-left">Created</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Retries</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {items.map((i) => (
                    <tr key={i.id} className={sel.has(i.id) ? "bg-blue-50/40" : ""} data-testid={`eq-row-${i.id}`}>
                      <td className="p-3"><Checkbox checked={sel.has(i.id)} onCheckedChange={() => toggle(i.id)} data-testid={`eq-check-${i.id}`} /></td>
                      <td className="p-3">
                        <div className="font-medium text-slate-800">{i.to}</div>
                        {i.is_demo && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-600"><AlertTriangle className="w-3 h-3" />{i.demo_reason}</span>}
                      </td>
                      <td className="p-3 text-slate-500">{i.type || "—"}</td>
                      <td className="p-3 text-slate-700 max-w-[220px] truncate">{i.subject}</td>
                      <td className="p-3 text-xs text-slate-500">
                        {i.client && <div>{i.client.name}</div>}
                        {i.work_order && <div>{i.work_order.wo_number}</div>}
                        {i.invoice && <div>{i.invoice.invoice_number}</div>}
                        {i.property && <div className="truncate max-w-[160px]">{i.property.name}</div>}
                        {!i.client && !i.work_order && !i.invoice && !i.property && "—"}
                      </td>
                      <td className="p-3 text-xs text-slate-500">{i.created_at ? new Date(i.created_at).toLocaleString() : "—"}</td>
                      <td className="p-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[i.status] || "bg-slate-100"}`}>{i.status}</span>
                        {i.error && <div className="text-[10px] text-rose-500 max-w-[160px] truncate mt-0.5">{i.error}</div>}
                      </td>
                      <td className="p-3 text-slate-500">{i.retry_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data && data.pages > 1 && (
            <div className="flex items-center justify-between p-3 border-t border-slate-100 text-sm">
              <span className="text-slate-500">Page {data.page} of {data.pages} · {data.total} total</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={data.page <= 1} onClick={() => { const p = data.page - 1; setPage(p); load(p); }} data-testid="eq-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" disabled={data.page >= data.pages} onClick={() => { const p = data.page + 1; setPage(p); load(p); }} data-testid="eq-next"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk confirm modal */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="eq-confirm-modal">
          <DialogHeader>
            <DialogTitle className="capitalize">{confirm?.action} {confirm?.count} email(s)?</DialogTitle>
            <DialogDescription>
              This will {confirm?.action} <b>{confirm?.count}</b> email(s) across <b>{confirm?.recipients}</b> recipient(s).
            </DialogDescription>
          </DialogHeader>
          {confirm?.action === "send" && confirm?.demoCount > 0 && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="eq-demo-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span><b>{confirm.demoCount}</b> of these appear to be <b>test/demo</b> recipients. Sending will deliver real emails to them. Review before proceeding.</span>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button onClick={runAction} className={confirm?.action === "delete" ? "bg-rose-600 hover:bg-rose-700" : "bg-blue-600 hover:bg-blue-700"} data-testid="eq-confirm-action">Confirm {confirm?.action}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete demo preview modal */}
      <Dialog open={!!demoPreview} onOpenChange={(o) => !o && setDemoPreview(null)}>
        <DialogContent data-testid="eq-demo-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-rose-600" />Delete Test/Demo Queue</DialogTitle>
            <DialogDescription>
              We detected <b>{demoPreview?.demo_count}</b> test/demo email(s) and <b>{demoPreview?.legit_count}</b> legitimate email(s).
              Only the <b>{demoPreview?.demo_count}</b> test/demo email(s) will be deleted. Legitimate emails are preserved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDemoPreview(null)}>Cancel</Button>
            <Button onClick={confirmDeleteDemo} disabled={busy || !demoPreview?.demo_count} className="bg-rose-600 hover:bg-rose-700" data-testid="eq-confirm-delete-demo">Delete {demoPreview?.demo_count} Test/Demo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit log modal */}
      <Dialog open={showAudit} onOpenChange={setShowAudit}>
        <DialogContent className="max-w-2xl" data-testid="eq-audit-modal">
          <DialogHeader><DialogTitle>Audit Log</DialogTitle>
            <DialogDescription>Record of who sent, deleted, cancelled or retried emails, and how many were affected.</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-auto divide-y divide-slate-50">
            {audit.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No actions logged yet.</p> : audit.map((a) => (
              <div key={a.id} className="py-2.5 text-sm flex items-center justify-between">
                <div><span className="font-semibold capitalize">{a.action.replace("_", " ")}</span> · {a.affected_count} affected
                  <div className="text-xs text-slate-400">{a.actor_email} · {new Date(a.at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

const Stat = ({ label, value, cls }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`text-2xl font-head font-bold ${cls || "text-slate-900"}`}>{value}</div>
  </div>
);

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-xs text-slate-500 block mb-1">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-40" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);
